import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import ChemSearchModal from 'chemistry/ChemSearchModal';
import {MoleculeImagePopover, MOLECULE_IMAGE_POPOVER_QUERY} from 'chemistry/molecules/MoleculeImagePopover';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import notifications from 'notifications';
import {Filter, FilterKey, SearchParams} from 'search/SearchConstants';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import {BLUE_LIGHT, BLUE_POWDER, NEAR_BLACK, ORANGE_POWDER} from 'ui-library/Colors';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import {FolderItemIcon} from 'ui-library/icons/IconConstants';
import legacyRunModal from 'ui-library/legacyRunModal';

import {
  applyChemSearchSubmit,
  getChemSearchChipLabel,
  isSubstructureSearch,
  ToggleHighlightParams,
} from './ChemSearchUtils';
import {
  MoleculeImagePopoverQuery,
  MoleculeImagePopoverQueryVariables,
} from './molecules/__generated/MoleculeImagePopoverQuery';

export interface ChemSearchChipProps {
  className?: string;
  onUpdateFilters: (filters: Array<Filter>) => void;
  searchParams: SearchParams;
  toggleHighlightParams: ToggleHighlightParams;
  mol: string;
  filterError: string | null;
}

export default function ChemSearchChip({
  className,
  onUpdateFilters,
  searchParams,
  toggleHighlightParams,
  mol,
  filterError,
}: ChemSearchChipProps): JSX.Element {
  const moleculeImagePopoverQueryResult = useBenchlingQuery<
    MoleculeImagePopoverQuery,
    MoleculeImagePopoverQueryVariables
  >(MOLECULE_IMAGE_POPOVER_QUERY, {
    variables: {mol},
  });
  if (moleculeImagePopoverQueryResult.type === QueryResultType.ERROR) {
    notifications.error('Failed to create image for query molecule. Please try again.');
  }
  const popoverToggleHighlightParams = {
    ...toggleHighlightParams,
    showToggleHighlightOption:
      toggleHighlightParams.showToggleHighlightOption && isSubstructureSearch(searchParams),
  };
  const popover =
    moleculeImagePopoverQueryResult.type === QueryResultType.SUCCESS ? (
      <MoleculeImagePopover
        toggleHighlightParams={popoverToggleHighlightParams}
        moleculeImage={moleculeImagePopoverQueryResult.data.moleculeImage}
      />
    ) : null;

  return (
    <Chip
      className={classNames(
        filterError ? css(styles.chipInvalid) : css(styles.chipValid),
        css(styles.chip),
        className
      )}
      size={ChipSize.TABLE_CELL}
      text={getChemSearchChipLabel(searchParams)}
      icon={FolderItemIcon.SmallMolecule}
      key="chemSearchChip"
      onClick={async () => {
        const chemSearchSubmit = await legacyRunModal(ChemSearchModal, {
          initialMol: mol,
          toggleHighlightParams,
          searchParams,
        });
        if (chemSearchSubmit != null) {
          onUpdateFilters(applyChemSearchSubmit(searchParams, chemSearchSubmit).filters);
        }
      }}
      onRemove={() => {
        onUpdateFilters(searchParams.filters.filter((filter) => filter.key !== FilterKey.CHEMICAL_STRUCTURE));
      }}
      popoverContent={filterError ? null : popover}
      popoverPlacement={FloatingPlacement.BOTTOM}
      tooltipTitle={filterError}
    />
  );
}

const styles = StyleSheet.create({
  chip: {
    marginRight: '0px',
  },
  chipValid: {
    backgroundColor: BLUE_POWDER,
    color: BLUE_LIGHT,
  },
  chipInvalid: {
    backgroundColor: ORANGE_POWDER,
    color: NEAR_BLACK,
  },
});
